if (!customElements.get('speakon-home-quotes')) {
  customElements.define('speakon-home-quotes', class extends HTMLElement {
    connectedCallback() {
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.events?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      const modal = this.querySelector('.speakon-quotes__modal');
      const video = modal.querySelector('.speakon-quotes__video');
      const original = modal.querySelector('.speakon-quotes__source');
      modal.addEventListener('show.bs.modal', event => {
        const trigger = event.relatedTarget;
        const author = trigger.dataset.reviewAuthor;
        const embed = this.reviewEmbed(trigger.href);
        original.href = trigger.href;
        modal.setAttribute('aria-label', `${author} — original review video`);
        video.replaceChildren();
        if (!embed) { video.textContent = 'This review is available on the original platform.'; return; }
        modal.dataset.format = embed.format;
        const iframe = document.createElement('iframe');
        iframe.title = `${author} — original review video`;
        iframe.src = embed.src;
        iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        video.appendChild(iframe);
      }, options);
      modal.addEventListener('hidden.bs.modal', () => {
        video.replaceChildren();
        delete modal.dataset.format;
      }, options);
      const track = this.querySelector('.speakon-quotes__grid');
      const cards = [...this.querySelectorAll('.speakon-quotes__card')];
      const dots = [...this.querySelectorAll('[data-quote-index]')];
      const position = this.querySelector('[data-quote-position]');
      const previous = this.querySelector('[data-quote-step="-1"]');
      const next = this.querySelector('[data-quote-step="1"]');
      // Use the last card, not scrollWidth: descendant overflow can extend WebKit's scroll range.
      const maxScroll = () => cards.length ? Math.max(0,
        track.scrollLeft + cards[cards.length - 1].getBoundingClientRect().right - track.getBoundingClientRect().right) : 0;
      const scrollTo = (left, behavior) => track.scrollTo({ left: Math.max(0, Math.min(left, maxScroll())), behavior });
      const goTo = index => {
        const distance = cards[index].getBoundingClientRect().left - track.getBoundingClientRect().left;
        scrollTo(track.scrollLeft + distance, reduced.matches ? 'instant' : 'smooth');
      };
      dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index), options));
      const update = () => {
        const end = maxScroll();
        if (track.scrollLeft > end + 1) scrollTo(end, 'instant');
        if (previous) previous.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= end - 1;
        const left = track.getBoundingClientRect().left;
        const index = cards.reduce((closest, card, current) =>
          Math.abs(card.getBoundingClientRect().left - left) < Math.abs(cards[closest].getBoundingClientRect().left - left) ? current : closest, 0);
        dots.forEach((dot, current) => dot.setAttribute('aria-current', String(current === index)));
        if (position) position.textContent = `${index + 1} / ${cards.length}`;
      };
      track.addEventListener('scroll', update, { ...options, passive: true });
      this.resize = new ResizeObserver(update);
      this.resize.observe(track);
      update();
      this.querySelectorAll('[data-quote-step]').forEach(button => button.addEventListener('click', () => {
        if (!cards.length) return;
        const distance = cards[0].offsetWidth + parseFloat(getComputedStyle(track).columnGap);
        scrollTo(track.scrollLeft + Number(button.dataset.quoteStep) * distance, reduced.matches ? 'instant' : 'smooth');
      }, options));
      this.addEventListener('shopify:block:select', event => {
        this.show();
        const index = cards.indexOf(event.target.closest('.speakon-quotes__card'));
        if (index >= 0) goTo(index);
      }, options);
      if (this.dataset.animate !== 'true' || reduced.matches || !window.IntersectionObserver) return;
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.remove('is-pending');
          this.observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -32px 0px' });
      this.querySelectorAll('[data-quote-reveal]').forEach(element => {
        element.classList.add('is-pending');
        this.observer.observe(element);
      });
      reduced.addEventListener('change', () => {
        if (reduced.matches) this.show();
      }, { signal: this.events.signal });
    }

    reviewEmbed(link) {
      let url;
      try { url = new URL(link); } catch { return null; }
      if (url.protocol !== 'https:') return null;
      const parts = url.pathname.split('/').filter(Boolean);
      if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be'].includes(url.hostname)) {
        const id = url.hostname === 'youtu.be' ? parts[0] :
          url.pathname === '/watch' ? url.searchParams.get('v') :
          ['embed', 'shorts'].includes(parts[0]) ? parts[1] : null;
        if (/^[\w-]{11}$/.test(id || '')) return { format: 'youtube', src: `https://www.youtube.com/embed/${id}?enablejsapi=1` };
      }
      if (['instagram.com', 'www.instagram.com'].includes(url.hostname) &&
          ['p', 'reel', 'tv'].includes(parts[0]) && /^[\w-]+$/.test(parts[1] || '')) {
        return { format: 'instagram', src: `https://www.instagram.com/${parts[0]}/${parts[1]}/embed/` };
      }
      return null;
    }

    show() {
      this.observer?.disconnect();
      this.querySelectorAll('.is-pending').forEach(element => element.classList.remove('is-pending'));
    }

    disconnectedCallback() { this.show(); this.events?.abort(); this.resize?.disconnect(); }
  });
}
